import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { nameValidation } from 'validators';
import { BottomSheetContentLayout, Column, FunctionalLoader, Grid, GridItem, Menu, TextField, Typography, useOverlayContext } from 'seagull';
import { Checkmark, Download, UserAdd } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { validateContact } from '~/business-logic/validators';
import { ErrorText } from '~/constants';
import { useEventReporter, useStore, useStoreActions } from '~/contexts';
import { useOnMount } from '~/hooks/useOnMount';
import { ErrorResponse, ServerError } from '~/infra/ErrorResponse';
import { UserRole } from '~/models';
import { selectors } from '~/store/state-selectors';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import styles from './InviteMemberBottomSheetFlow.module.scss';
const testPrefix = 'invite-member-bottom-sheet';
const defaultRole = UserRole.Member;
export const GetInviteMemberBottomSheetTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            InviteMemberBottomSheetTID: {
                DownloadPdf: 'cover-page-download-pdf',
                InviteMember: 'cover-page-invite-member',
                InputFirstName: 'invite-member-bottom-sheet-input-first-name',
                InputLastName: 'invite-member-bottom-sheet-input-last-name',
                InputEmail: 'invite-member-bottom-sheet-input-email',
                InputRole: 'invite-member-bottom-sheet-input-role',
                RoleOptOwner: 'invite-member-bottom-sheet-role-opt-owner',
                RoleOptMember: 'invite-member-bottom-sheet-role-opt-member',
                SendInvitation: 'invite-member-bottom-sheet-send-invitation'
            }
        }
    });
const ROLE_OPTIONS = [
    {
        value: UserRole.Owner,
        text: 'Owner',
        subtext: 'Has access to everything'
    },
    {
        value: UserRole.Member,
        text: 'Member',
        subtext: 'Can view everything but cannot make any changes except for adding a payment method.'
    }
];
function InviteMemberRoleSelectionPage(param) {
    let { onSelect, disableOwner } = param;
    const { popBottomSheetPage } = useOverlayContext();
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        contentGutter: "m",
        header: {
            title: 'Select a role'
        },
        children: /*#__PURE__*/ _jsx(Menu, {
            classes: {
                root: styles.roleSelectionMenu
            },
            dimensions: {
                width: 'auto',
                paddingTop: 16
            },
            items: ROLE_OPTIONS.map((param)=>{
                let { value, text, subtext } = param;
                return {
                    disabled: disableOwner && value === UserRole.Owner,
                    id: value,
                    title: text,
                    body: subtext,
                    testId: "".concat(testPrefix, "-role-opt-").concat(value),
                    onClick: ()=>{
                        onSelect(value);
                        popBottomSheetPage();
                    }
                };
            })
        })
    });
}
function InviteMemberFormPage(param) {
    let { onSuccess } = param;
    var _ref;
    var _ROLE_OPTIONS_find;
    const { pushBottomSheetPage, hideBottomSheet, showErrorToast } = useOverlayContext();
    const worksAt = useStore(selectors.user.business);
    const userRole = useStore(selectors.user.role);
    const { setWorksAt, api, logBI } = useStoreActions();
    const { reportFormSubmission, prepareFormObject } = useEventReporter();
    const { control, formState, handleSubmit, setValue, watch } = useForm({
        mode: 'all',
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            role: ''
        }
    });
    const [existingMembers, setExistingMembers] = useState();
    const [inviteSuccess, setInviteSuccess] = useState(false);
    const firstNameValidator = nameValidation('First name', {
        required: 'Please enter a first name'
    });
    const lastNameValidator = nameValidation('Last name', {
        required: 'Please enter a last name'
    });
    const roleValidator = nameValidation('Role', {
        required: 'Please enter a role'
    });
    const selectedRole = watch('role');
    const roleLabel = selectedRole ? (_ref = (_ROLE_OPTIONS_find = ROLE_OPTIONS.find((o)=>o.value === selectedRole)) === null || _ROLE_OPTIONS_find === void 0 ? void 0 : _ROLE_OPTIONS_find.text) !== null && _ref !== void 0 ? _ref : selectedRole : 'Select a role';
    useOnMount(()=>{
        async function retrieveMembers() {
            try {
                const { members } = await api.member.getAll();
                setExistingMembers(members);
            } catch (unused) {
                setExistingMembers([]);
            }
        }
        retrieveMembers();
    });
    const emailRules = ()=>({
            validate: {
                email: validateContact.email,
                emailAlreadyUsed: (email)=>(existingMembers === null || existingMembers === void 0 ? void 0 : existingMembers.every((m)=>m.email() !== email)) || 'This email already belongs to a member of your team'
            }
        });
    const onValidSubmit = async (param)=>{
        let { firstName, lastName, email, role } = param;
        try {
            const invitedRole = role || defaultRole;
            logBI(12201, {
                email,
                role: invitedRole
            });
            await api.member.invite({
                firstName,
                lastName,
                email,
                role: invitedRole
            });
            if (worksAt) {
                setWorksAt(_object_spread_props(_object_spread({}, worksAt), {
                    membersCount: worksAt.membersCount + 1
                }));
            }
            reportFormSubmission('team-invited-member', [
                prepareFormObject('email', email),
                prepareFormObject('firstName', firstName),
                prepareFormObject('lastName', lastName)
            ]);
            onSuccess();
            setInviteSuccess(true);
            setTimeout(()=>hideBottomSheet(), 2000);
        } catch (err) {
            const e = new ErrorResponse(err);
            const text = e.hasServerError(ServerError.PendingInvitationAlreadyExists) ? 'There is already a pending invitation for this email' : e.hasServerError([
                ServerError.UserAlreadyAffiliatedWithBusiness,
                ServerError.UserAlreadyInBusiness
            ]) ? 'This email already belongs to a member of your team' : ErrorText.UnexpectedErrorTryAgain;
            showErrorToast(text);
        }
    };
    const openRoleSelection = ()=>{
        pushBottomSheetPage({
            content: /*#__PURE__*/ _jsx(InviteMemberRoleSelectionPage, {
                onSelect: (role)=>{
                    setValue('role', role, {
                        shouldValidate: true
                    });
                },
                disableOwner: userRole !== UserRole.Owner && userRole !== UserRole.AnchorAdmin
            })
        });
    };
    if (!existingMembers) {
        return /*#__PURE__*/ _jsx(FunctionalLoader, {
            show: true,
            name: "fetch-members-loader"
        });
    }
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        contentGutter: "m",
        header: {
            title: 'Invite a team member'
        },
        footer: {
            paddingTop: 8,
            primaryAction: inviteSuccess ? {
                text: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Checkmark, {}),
                        ' Invitation sent'
                    ]
                }),
                disabled: true
            } : {
                text: 'Send Invitation',
                disabled: formState.isSubmitting,
                testId: "".concat(testPrefix, "-send-invitation"),
                onClick: handleSubmit(onValidSubmit)
            }
        },
        children: /*#__PURE__*/ _jsxs(Grid, {
            className: styles.formContent,
            children: [
                /*#__PURE__*/ _jsx(GridItem, {
                    span: 12,
                    children: /*#__PURE__*/ _jsx(Controller, {
                        control: control,
                        name: "firstName",
                        rules: firstNameValidator,
                        render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                                collapseHelperText: false,
                                label: "First name",
                                testId: "".concat(testPrefix, "-input-first-name")
                            }, connectControllerToTextField(renderProps), firstNameValidator.dataAttr))
                    })
                }),
                /*#__PURE__*/ _jsx(GridItem, {
                    span: 12,
                    children: /*#__PURE__*/ _jsx(Controller, {
                        control: control,
                        name: "lastName",
                        rules: lastNameValidator,
                        render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                                collapseHelperText: false,
                                label: "Last name",
                                testId: "".concat(testPrefix, "-input-last-name")
                            }, connectControllerToTextField(renderProps), lastNameValidator.dataAttr))
                    })
                }),
                /*#__PURE__*/ _jsx(GridItem, {
                    span: 12,
                    children: /*#__PURE__*/ _jsx(Controller, {
                        control: control,
                        name: "email",
                        rules: emailRules(),
                        render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                                collapseHelperText: false,
                                label: "Email",
                                testId: "".concat(testPrefix, "-input-email")
                            }, connectControllerToTextField(renderProps)))
                    })
                }),
                /*#__PURE__*/ _jsx(GridItem, {
                    span: 12,
                    children: /*#__PURE__*/ _jsx(Controller, {
                        control: control,
                        name: "role",
                        rules: roleValidator,
                        render: (param)=>{
                            let { field, fieldState } = param;
                            var _fieldState_error;
                            return /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        className: styles.roleSelector,
                                        onClick: openRoleSelection,
                                        "data-testid": "".concat(testPrefix, "-input-role"),
                                        "data-error": fieldState.invalid ? 'true' : undefined,
                                        children: /*#__PURE__*/ _jsx(Typography, {
                                            variant: "paragraph1",
                                            skin: field.value ? undefined : 'secondary',
                                            weight: "normal",
                                            children: roleLabel
                                        })
                                    }),
                                    ((_fieldState_error = fieldState.error) === null || _fieldState_error === void 0 ? void 0 : _fieldState_error.message) && /*#__PURE__*/ _jsx(Typography, {
                                        variant: "paragraph3",
                                        skin: "error",
                                        className: styles.errorText,
                                        children: fieldState.error.message
                                    })
                                ]
                            });
                        }
                    })
                })
            ]
        })
    });
}
export function InviteMemberActionsMenu(param) {
    let { onDownloadPdf } = param;
    const { pushBottomSheetPage, hideBottomSheet } = useOverlayContext();
    const items = [
        {
            id: 'download-pdf',
            title: 'Download PDF',
            prefixIcon: /*#__PURE__*/ _jsx(Download, {}),
            testId: 'cover-page-download-pdf',
            onClick: ()=>{
                onDownloadPdf();
                hideBottomSheet();
            }
        },
        {
            id: 'invite-member',
            title: 'Invite a team member',
            prefixIcon: /*#__PURE__*/ _jsx(UserAdd, {}),
            testId: 'cover-page-invite-member',
            onClick: ()=>{
                pushBottomSheetPage({
                    content: /*#__PURE__*/ _jsx(InviteMemberFormPage, {
                        onSuccess: ()=>{}
                    })
                });
            }
        }
    ];
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        children: /*#__PURE__*/ _jsx(Column, {
            className: styles.actionsMenu,
            children: /*#__PURE__*/ _jsx(Menu, {
                classes: {
                    root: styles.actionsMenuRoot
                },
                dimensions: {
                    width: 'auto',
                    paddingTop: 16
                },
                items: items
            })
        })
    });
}
